import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { useAuth } from "@/lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckSquare, Square, Plus, Trash2, Calendar, RefreshCcw, ListChecks } from "lucide-react";
import { formatDate } from "@/lib/format";

const inputCls = "rounded-xl h-11";
const selectCls =
  "rounded-xl h-11 w-full px-3 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none";

function bucketFor(task, todayISO, weekEndISO) {
  if (task.status === "done") return "done";
  const due = task.due_date;
  if (!due) return "later";
  if (due < todayISO) return "overdue";
  if (due === todayISO) return "today";
  if (due <= weekEndISO) return "this_week";
  return "later";
}

export default function AdminMyTasks() {
  const { admin } = useAuth();
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { if (admin?.id && !ownerFilter) setOwnerFilter(admin.id); }, [admin, ownerFilter]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ownerFilter) params.owner_id = ownerFilter;
      const [tRes, teamRes] = await Promise.all([
        api.get("/admin/tasks", { params }),
        api.get("/admin/team").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(tRes.data.items || []);
      setTeam(teamRes.data.items || []);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [ownerFilter]);

  useEffect(() => { if (ownerFilter) fetchAll(); }, [fetchAll, ownerFilter]);

  const { overdue, today, thisWeek, later, done } = useMemo(() => {
    const todayDate = new Date();
    const todayISO = todayDate.toISOString().slice(0, 10);
    const weekEnd = new Date(todayDate);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const weekEndISO = weekEnd.toISOString().slice(0, 10);
    const groups = { overdue: [], today: [], this_week: [], later: [], done: [] };
    items.forEach((t) => groups[bucketFor(t, todayISO, weekEndISO)].push(t));
    return {
      overdue: groups.overdue,
      today: groups.today,
      thisWeek: groups.this_week,
      later: groups.later,
      done: groups.done,
    };
  }, [items]);

  const toggle = async (task) => {
    const nextStatus = task.status === "done" ? "pending" : "done";
    try {
      const { data } = await api.patch(`/admin/tasks/${task.id}`, { status: nextStatus });
      setItems((arr) => arr.map((x) => (x.id === task.id ? data : x)));
    } catch {
      toast.error("Failed to update");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/tasks/${toDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== toDelete.id));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setToDelete(null);
    }
  };

  const openCount = items.filter((t) => t.status !== "done").length;

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-tasks-page">
      <AdminHeader />

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-brand-orange font-semibold">Follow-ups</p>
            <h1 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-1" data-testid="admin-tasks-heading">
              My Tasks
            </h1>
            <p className="mt-2 text-sm text-brand-slate/75">
              {openCount} open · {done.length} completed
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-white rounded-full transition-colors"
              data-testid="tasks-refresh"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 bg-brand-orange text-white px-5 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full transition-colors"
              data-testid="tasks-new"
            >
              <Plus size={14} /> New task
            </button>
          </div>
        </div>

        <div className="bg-white border border-brand-navy/10 p-4 rounded-2xl mb-6">
          <div className="flex items-center gap-3">
            <Label className="text-xs text-brand-slate/70 uppercase tracking-wider whitespace-nowrap">Viewing tasks for</Label>
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className={selectCls + " max-w-xs"} data-testid="tasks-filter-owner">
              {team.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name || m.email}{m.id === admin?.id ? " (Me)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && <p className="text-sm text-brand-slate/60">Loading…</p>}

        <div className="space-y-6">
          <Bucket title="Overdue" tone="danger" items={overdue} onToggle={toggle} onDelete={setToDelete} testid="bucket-overdue" />
          <Bucket title="Due Today" tone="accent" items={today} onToggle={toggle} onDelete={setToDelete} testid="bucket-today" />
          <Bucket title="This Week" items={thisWeek} onToggle={toggle} onDelete={setToDelete} testid="bucket-this-week" />
          <Bucket title="Later" items={later} onToggle={toggle} onDelete={setToDelete} testid="bucket-later" />
          <Bucket title="Completed" muted items={done} onToggle={toggle} onDelete={setToDelete} testid="bucket-done" />
        </div>
      </main>

      <AddTaskModal
        open={showAdd}
        onOpenChange={setShowAdd}
        team={team}
        defaultOwnerId={ownerFilter || admin?.id}
        onCreated={(t) => setItems((arr) => [t, ...arr])}
      />

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title="Delete this task?"
        description={toDelete?.title}
        confirmLabel="Delete"
        destructive
        onConfirm={doDelete}
      />
    </div>
  );
}

function Bucket({ title, items, onToggle, onDelete, tone, muted, testid }) {
  if (!items.length) return null;
  const headerCls =
    tone === "danger" ? "text-red-700" :
    tone === "accent" ? "text-brand-orange" :
    "text-brand-navy";
  return (
    <section data-testid={testid}>
      <h2 className={`text-xs font-semibold uppercase tracking-[0.25em] mb-3 ${headerCls}`}>
        {title} · {items.length}
      </h2>
      <ul className="space-y-2">
        {items.map((t) => (
          <li
            key={t.id}
            className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
              muted ? "bg-brand-cream/40 border-brand-navy/10 opacity-70" :
              t.is_overdue ? "bg-red-50 border-red-200" :
              "bg-white border-brand-navy/10 hover:border-brand-orange/40"
            }`}
            data-testid={`mytask-item-${t.id}`}
          >
            <button
              onClick={() => onToggle(t)}
              className="mt-0.5 shrink-0"
              aria-label={t.status === "done" ? "Mark pending" : "Mark done"}
              data-testid={`mytask-toggle-${t.id}`}
            >
              {t.status === "done"
                ? <CheckSquare size={18} className="text-emerald-600" />
                : <Square size={18} className="text-brand-slate/60 hover:text-brand-orange" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${t.status === "done" ? "line-through text-brand-slate/60" : "text-brand-navy"}`}>
                {t.title}
              </p>
              {t.description && (
                <p className="text-xs text-brand-slate/70 mt-0.5 whitespace-pre-wrap">{t.description}</p>
              )}
              <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-brand-slate/60">
                {t.due_date && (
                  <span className={`inline-flex items-center gap-1 ${t.is_overdue ? "text-red-600 font-semibold" : ""}`}>
                    <Calendar size={10} /> {formatDate(t.due_date)}
                  </span>
                )}
                {t.owner_name && <span>· {t.owner_name}</span>}
                {t.deal_id && t.deal_title && (
                  <Link to={`/admin/pipeline/${t.deal_id}`} className="text-brand-orange hover:underline">· {t.deal_title}</Link>
                )}
              </div>
            </div>
            <button
              onClick={() => onDelete(t)}
              className="text-brand-slate/40 hover:text-red-600"
              aria-label="Delete"
              data-testid={`mytask-delete-${t.id}`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AddTaskModal({ open, onOpenChange, team, defaultOwnerId, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [ownerId, setOwnerId] = useState(defaultOwnerId || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open && defaultOwnerId && !ownerId) setOwnerId(defaultOwnerId); }, [open, defaultOwnerId, ownerId]);

  const reset = () => { setTitle(""); setDescription(""); setDueDate(""); setOwnerId(defaultOwnerId || ""); setSaving(false); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload = { title: title.trim() };
      if (description.trim()) payload.description = description.trim();
      if (dueDate) payload.due_date = dueDate;
      if (ownerId) payload.owner_id = ownerId;
      const { data } = await api.post("/admin/tasks", payload);
      toast.success("Task added");
      onCreated?.(data);
      close(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg" data-testid="mytasks-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy flex items-center gap-2"><ListChecks size={18} className="text-brand-orange" /> New task</DialogTitle>
          <DialogDescription>Create a follow-up so nothing slips through.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Title <span className="text-brand-orange">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required className={inputCls} data-testid="mytasks-input-title" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm text-brand-navy">Description</Label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="rounded-xl w-full px-3 py-2 text-sm bg-white border border-gray-300 text-brand-navy focus:border-brand-orange focus:outline-none" data-testid="mytasks-input-description" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm text-brand-navy">Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} data-testid="mytasks-input-due" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm text-brand-navy">Assigned to</Label>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className={selectCls} data-testid="mytasks-input-owner">
                {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => close(false)} className="inline-flex items-center gap-2 border border-brand-navy/20 px-5 py-2.5 text-xs font-semibold text-brand-navy hover:bg-brand-cream rounded-full">Cancel</button>
            <button type="submit" disabled={saving || !title.trim()} className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-2.5 text-xs font-semibold hover:bg-brand-orange-hover rounded-full disabled:opacity-60" data-testid="mytasks-submit">
              {saving ? "Adding…" : "Add task"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
